/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import * as React from "react";
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IColumn,
    MessageBarType,
    PrimaryButton,
    SelectionMode,
    TextField
} from "@fluentui/react";
import { IStringViewProps, IStringViewState, StringViewCommands } from "./StringViewViewModels";
import Grid from "../../Grid/Grid";
import { IGridActionsProps, IGridItem } from "../../Grid/GridModels";
import styles from "./StringView.module.css";
import { CancelButtonWrapper, SaveButtonWrapper } from "../../Grid/GridCommandBar";
import { useBoolean } from "@fluentui/react-hooks";
import { postMessage } from "@nuance/hux-components";

const stringViewCommands = new StringViewCommands();
const cmds = stringViewCommands.Cmds;

const StringView = (props: IStringViewProps): JSX.Element => {
    if (props.definition === undefined) {
        throw new Error("Definition should not be undefined here.");
    }

    const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);

    const initialState: IStringViewState = {
        definition: props.definition,
        draftDefinition: props.definition,
        editMode: false,
        isDirty: false,
        saved: false,
        missingLanguage: stringViewCommands.hasMissingTranslations(props.definition)
    };

    const [state, dispatch] = React.useReducer(stringViewCommands.Reducer, initialState);

    React.useEffect(() => {
        props.advancedViewVM.dispatch(props.advancedViewVM.cmds.definitionChange(state.definition));
    }, [state.definition]);

    const items = React.useMemo(
        () =>
            state.draftDefinition.UI.StringResources.slice(0)
                .map((stringResource, index) => {
                    const { Id, ...rest } = stringResource;
                    const item: IGridItem = {
                        id: Id,
                        index,
                        ...rest
                    };
                    return item;
                })
                .sort((a: IGridItem, b: IGridItem) => (a.id < b.id ? -1 : 1)),
        [state.draftDefinition]
    );

    const tableColumns: IColumn[] = [
        {
            key: "nameCol",
            name: "Setting name",
            fieldName: "id",
            minWidth: 200,
            maxWidth: 300,
            isResizable: true
        },
        {
            key: "langCol",
            name: "Language",
            fieldName: "Language",
            minWidth: 100,
            maxWidth: 100,
            isResizable: true
        },
        {
            key: "textCol",
            name: "Text",
            fieldName: "Text",
            minWidth: 200,
            isResizable: true,
            className: state.editMode ? styles.editTextCell : undefined,
            onRender: (item: IGridItem) => (
                <>
                    {state.editMode && (
                        <TextField
                            className={styles.nonEditTextBox}
                            value={item.Text.toString()}
                            onChange={(e, value) =>
                                dispatch(cmds.onTextChanged({ item, text: value ?? "" }))
                            }
                        />
                    )}
                    {!state.editMode && (
                        <div
                            className={styles.editTextBox}
                            onClick={() => dispatch(cmds.onItemClick())}
                        >
                            {item.Text}
                        </div>
                    )}
                </>
            )
        }
    ];

    /** CommandBar items */
    const addAction = {
        key: "add",
        text: "Add fr_FR",
        iconProps: { iconName: "Add" },
        disabled: !state.missingLanguage,
        onClick: () => dispatch(cmds.addLanguage("fr-FR"))
    };

    const cancelAction = {
        key: "cancel",
        text: "Cancel",
        commandBarButtonAs: CancelButtonWrapper,
        buttonStyles: { root: styles.cancelButton },
        onClick: () => {
            if (state.isDirty) {
                toggleHideDialog(); // open confirmation dialog
            } else {
                dispatch(cmds.onCancel());
            }
        }
    };

    const saveAction = {
        key: "save",
        text: "Save changes",
        disabled: !state.isDirty,
        commandBarButtonAs: SaveButtonWrapper,
        onClick: () => {
            dispatch(cmds.onSave());
            postMessage({
                message: "Your changes have been saved",
                timeout: 5,
                messageBarType: MessageBarType.success
            });
        }
    };

    let actions: IGridActionsProps;
    if (state.editMode) {
        actions = {
            left: [addAction],
            right: [cancelAction, saveAction]
        };
    } else {
        actions = {
            left: [addAction]
        };
    }

    const dialogContentProps = {
        type: DialogType.normal,
        title: "Unsaved changes",
        subText: "Your changes will be lost if you continue."
    };

    const onDialogConfirm = () => {
        dispatch(cmds.onCancel()); // cancel edit button clicked
        toggleHideDialog();
    };

    const onDialogClose = () => {
        toggleHideDialog();
    };

    return (
        <>
            <Grid
                id="stringResources"
                columns={tableColumns}
                items={items}
                hasFilter
                filterColumns={["id", "Language", "Text"]}
                selectionMode={SelectionMode.none}
                actions={actions}
                onSelectRow={() => dispatch(cmds.onItemClick())}
            ></Grid>

            <Dialog
                hidden={hideDialog}
                onDismiss={onDialogClose}
                dialogContentProps={dialogContentProps}
                modalProps={{ isBlocking: true }}
                maxWidth={560}
                minWidth={560}
            >
                <DialogFooter>
                    <PrimaryButton onClick={onDialogConfirm} text="Continue" />
                    <DefaultButton onClick={onDialogClose} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default StringView;
